import { z } from 'zod';

import { noop } from '../../submodules/shared/utils/noop';

/**
 * Hostname / FQDN: one or more dot-separated labels of letters, digits, and
 * inner hyphens. Permissive on purpose — accepts single-label hostnames.
 */
const FQDN_PATTERN = /^(?=.{1,253}$)[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/;

/**
 * Non-empty trimmed string.
 * @param {string} label field label used in the error message
 * @return {object} Zod schema
 */
export const requiredString = (label) =>
    z
        .string({ error: `${label} is required` })
        .trim()
        .min(1, `${label} is required`);

/**
 * Email address.
 * @param {string} message error message
 * @return {object} Zod schema
 */
export const email = (message = 'Enter a valid email address') => z.email({ error: message });

/**
 * IPv4 address.
 * @param {string} message error message
 * @return {object} Zod schema
 */
export const ipv4 = (message = 'Enter a valid IPv4 address (e.g. 10.0.0.1)') => z.ipv4({ error: message });

/**
 * Fully qualified domain name / hostname.
 * @param {string} message error message
 * @return {object} Zod schema
 */
export const fqdn = (message = 'Enter a valid hostname') => z.string({ error: message }).regex(FQDN_PATTERN, message);

/**
 * String of digits only (ids typed into text inputs); numbers loaded from
 * the API are accepted and treated as their string form.
 * @param {string} label field label used in the error messages
 * @return {object} Zod schema
 */
export const numericId = (label) =>
    z.preprocess(
        (value) => (typeof value === 'number' ? String(value) : value),
        z
            .string({ error: `${label} is required` })
            .trim()
            .min(1, `${label} is required`)
            .regex(/^\d+$/, `${label} must contain only digits`),
    );

/**
 * Any non-empty selection (select inputs whose values may be strings or
 * numbers).
 * @param {string} label field label used in the error message
 * @return {object} Zod schema
 */
export const requiredSelection = (label) => z.custom((value) => (value ?? '') !== '', `${label} is required`);

/**
 * Date value (Date instance or parseable string); rejects empty/null.
 * @param {string} label field label used in the error message
 * @return {object} Zod schema
 */
export const requiredDate = (label) =>
    z.preprocess(
        (value) => (value === null || value === '' ? noop() : value),
        z.coerce.date({ error: `${label} is required` }),
    );

/**
 * Allow a field to be empty ('' / null / undefined / key missing) or
 * satisfy the rule. The `.optional()` matters: Zod 4 rejects missing
 * object keys unless the field schema is explicitly optional, even when
 * it accepts undefined as a value.
 * @param {object} rule Zod schema applied when a value is present
 * @return {object} Zod schema
 */
export const emptyOr = (rule) => z.union([z.literal(''), z.null(), z.undefined(), rule]).optional();

/**
 * Cross-field check that an end date does not precede a start date; attach
 * via `.superRefine(dateOrder('start_field', 'end_field'))`. Skips when
 * either side is missing or unparseable — presence is its own rule.
 * @param {string} startField name of the start date field
 * @param {string} endField name of the end date field
 * @param {string} message error message attached to the end field
 * @return {Function} superRefine callback
 */
export const dateOrder =
    (startField, endField, message = 'End date must not be before the start date') =>
    (data, ctx) => {
        const start = new Date(data[startField]).getTime();
        const end = new Date(data[endField]).getTime();

        if (!data[startField] || !data[endField] || Number.isNaN(start) || Number.isNaN(end)) {
            return;
        }

        if (end < start) {
            ctx.addIssue({ code: 'custom', path: [endField], message });
        }
    };
